// frontend/src/modules/integrations/pages/IntegrationsPage.tsx
//
// Settings → Integrations. Currently a single card: QuickBooks Online.
// Connect kicks off the Intuit OAuth redirect (window.location = authorize
// url); the browser comes back to /dashboard/settings/integrations with
// ?quickbooks=connected|error which we toast and strip from the URL.

import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { PlugZap, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { LoadingState } from '../../../design-system/components/LoadingState';
import { Button } from '../../../components/ui/button';
import { useConfirm } from '../../../components/ui/confirm-dialog';
import { VISUAL, statusPill, statusDot } from '../../../design-system/visual';
import { formatDateTime } from '../../../design-system/format';
import { usePermission } from '../../../core/auth/usePermission';
import { quickbooksApi } from '../api';
import { ApiError } from '../../../core/api-client';
import type { QuickBooksStatus } from '../types';

export function IntegrationsPage() {
  const confirm = useConfirm();
  const canManage = usePermission('settings.write');
  const [status, setStatus] = React.useState<QuickBooksStatus | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [notConfigured, setNotConfigured] = React.useState(false);
  const [connecting, setConnecting] = React.useState(false);
  const [testing, setTesting] = React.useState(false);
  const [disconnecting, setDisconnecting] = React.useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const loadStatus = React.useCallback(() => {
    setLoading(true);
    quickbooksApi.status()
      .then((s) => { setStatus(s); setNotConfigured(false); })
      .catch((err) => {
        if (err instanceof ApiError && err.status === 503) setNotConfigured(true);
      })
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => { loadStatus(); }, [loadStatus]);

  // Toast + strip the ?quickbooks= result param from the OAuth redirect.
  React.useEffect(() => {
    const result = searchParams.get('quickbooks');
    if (!result) return;
    if (result === 'connected') {
      toast.success('QuickBooks connected');
      loadStatus();
    } else if (result === 'error') {
      toast.error('QuickBooks connection failed', { description: 'Please try connecting again.' });
    }
    const next = new URLSearchParams(searchParams);
    next.delete('quickbooks');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleConnect() {
    setConnecting(true);
    try {
      const { url } = await quickbooksApi.connect();
      window.location.href = url;
    } catch (err: any) {
      toast.error('Could not start QuickBooks connection', { description: err.message });
      setConnecting(false);
    }
  }

  async function handleTest() {
    setTesting(true);
    try {
      const result = await quickbooksApi.test();
      toast.success(`Connected to ${result.company_name}`);
    } catch (err: any) {
      toast.error('Test connection failed', { description: err.message });
    } finally {
      setTesting(false);
    }
  }

  async function handleDisconnect() {
    const ok = await confirm({
      title: 'Disconnect QuickBooks?',
      description: 'Worqly will stop pushing invoices to QuickBooks until you reconnect.',
      confirmLabel: 'Disconnect',
      destructive: true,
    });
    if (!ok) return;
    setDisconnecting(true);
    try {
      await quickbooksApi.disconnect();
      toast.success('QuickBooks disconnected');
      loadStatus();
    } catch (err: any) {
      toast.error('Disconnect failed', { description: err.message });
    } finally {
      setDisconnecting(false);
    }
  }

  return (
    <div className="flex-1 min-w-0">
      <PageHeader eyebrow="Settings" title="Integrations" description="Connect Worqly to the other tools your business runs on." />

      <div className="px-6 sm:px-8 lg:px-10 pb-12 max-w-3xl">
        {loading ? (
          <LoadingState variant="inline" />
        ) : (
          <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-[#2CA01C]/10 flex items-center justify-center shrink-0">
                  <PlugZap className="h-5 w-5 text-[#2CA01C]" />
                </div>
                <div>
                  <h3 className={VISUAL.dashSection}>QuickBooks Online</h3>
                  <p className="text-[12.5px] text-muted-foreground mt-0.5">
                    Push Worqly invoices to QuickBooks as you send them.
                  </p>
                </div>
              </div>
              {!notConfigured && status && (
                status.connected
                  ? status.reauth_required
                    ? <span className={statusPill('warning')}><span className={statusDot('warning')} />Reconnect required</span>
                    : <span className={statusPill('success')}><span className={statusDot('success')} />Connected</span>
                  : <span className={statusPill('muted')}><span className={statusDot('muted')} />Not connected</span>
              )}
            </div>

            {notConfigured ? (
              <div className="flex items-start gap-2 rounded-xl border border-border bg-muted/40 p-4 text-[13px] text-muted-foreground">
                <XCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>QuickBooks integration isn't configured for this environment yet. Contact support.</span>
              </div>
            ) : status?.connected ? (
              <div className="space-y-4">
                {status.reauth_required ? (
                  <div className="flex items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-[13px]">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-500" />
                    <span>
                      Your QuickBooks authorisation has expired, so pushes are paused until you reconnect.
                      Intuit expires the connection after about 100 days without use — reconnecting takes a few seconds
                      and keeps everything already synced.
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                    <span>
                      Connected{status.connected_at ? ` on ${formatDateTime(status.connected_at)}` : ''}
                      {status.realm_id ? <span className="font-mono text-[12px] ml-1">(realm {status.realm_id})</span> : null}
                    </span>
                  </div>
                )}
                {canManage && (
                  <div className="flex items-center gap-2">
                    {status.reauth_required ? (
                      <Button className="rounded-full h-10 gap-1.5" onClick={handleConnect} disabled={connecting}>
                        <PlugZap className="h-3.5 w-3.5" /> {connecting ? 'Redirecting…' : 'Reconnect QuickBooks'}
                      </Button>
                    ) : (
                      <Button variant="outline" className="rounded-full h-10" onClick={handleTest} disabled={testing}>
                        {testing ? 'Testing…' : 'Test connection'}
                      </Button>
                    )}
                    <Button variant="outline" className="rounded-full h-10 text-destructive hover:text-destructive" onClick={handleDisconnect} disabled={disconnecting}>
                      {disconnecting ? 'Disconnecting…' : 'Disconnect'}
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-[13px] text-muted-foreground leading-relaxed">
                  Connect your QuickBooks Online account to push invoices as you send them from Worqly.
                </p>
                {canManage ? (
                  <Button className="rounded-full h-10 gap-1.5" onClick={handleConnect} disabled={connecting}>
                    <PlugZap className="h-3.5 w-3.5" /> {connecting ? 'Redirecting…' : 'Connect QuickBooks'}
                  </Button>
                ) : (
                  <p className="text-[12.5px] text-muted-foreground">Ask a workspace admin to connect QuickBooks.</p>
                )}
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
